import { useState, type FormEvent } from 'react';
import {
  loginUserViaApi,
  setPasswordViaApi,
  signupUserViaApi,
  type AuthenticatedUserSession,
} from '../../services/apiClient';
import { InfoPopover } from '../InfoPopover/InfoPopover';
import styles from './AuthGateView.module.css';

export type AuthScreenMode = 'LOGIN' | 'REGISTER' | 'SET_PASSWORD';

interface AuthGateViewProps {
  readonly initialMode?: AuthScreenMode;
  readonly initialEmailFromLink?: string;
  readonly initialTokenFromLink?: string;
  readonly onAuthenticated: (session: AuthenticatedUserSession) => void;
}

export function AuthGateView({
  initialMode = 'LOGIN',
  initialEmailFromLink = '',
  initialTokenFromLink = '',
  onAuthenticated,
}: AuthGateViewProps) {
  const [mode, setMode] = useState<AuthScreenMode>(initialMode);

  // Estado de Inicio de Sesión
  const [loginEmail, setLoginEmail] = useState<string>(initialEmailFromLink);
  const [loginPassword, setLoginPassword] = useState<string>('');

  // Estado de Registro Convencional (sin contraseña)
  const [fullName, setFullName] = useState<string>('');
  const [registerEmail, setRegisterEmail] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [occupation, setOccupation] = useState<string>('');
  const [signupSuccessEmail, setSignupSuccessEmail] = useState<string | null>(null);
  const [isRestrictedPublicAttempt, setIsRestrictedPublicAttempt] =
    useState<boolean>(false);

  // Estado de Creación de Contraseña desde el Link enviado por Resend
  const [setupEmail, setSetupEmail] = useState<string>(initialEmailFromLink);
  const [setupToken] = useState<string>(initialTokenFromLink);
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const switchMode = (nextMode: AuthScreenMode) => {
    setMode(nextMode);
    setErrorMessage(null);
    setIsRestrictedPublicAttempt(false);
    setSignupSuccessEmail(null);
  };

  const handleLoginSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    setIsRestrictedPublicAttempt(false);
    setIsSubmitting(true);

    const res = await loginUserViaApi({
      email: loginEmail.trim(),
      password: loginPassword,
    });

    setIsSubmitting(false);

    if (!res.ok) {
      if (res.code === 'ERR_PILOT_EMAIL_NOT_AUTHORIZED') {
        setIsRestrictedPublicAttempt(true);
      }
      setErrorMessage(res.message);
      return;
    }

    onAuthenticated(res.session);
  };

  const handleRegisterSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    setIsRestrictedPublicAttempt(false);
    setSignupSuccessEmail(null);
    setIsSubmitting(true);

    const res = await signupUserViaApi({
      fullName: fullName.trim(),
      email: registerEmail.trim(),
      phone: phone.trim() || undefined,
      occupation: occupation.trim() || undefined,
      appBaseUrl: window.location.origin,
    });

    setIsSubmitting(false);

    if (!res.ok) {
      if (res.code === 'ERR_PILOT_EMAIL_NOT_AUTHORIZED') {
        setIsRestrictedPublicAttempt(true);
      } else {
        setErrorMessage(res.message);
      }
      return;
    }

    setSignupSuccessEmail(res.email);
  };

  const handleSetPasswordSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    if (newPassword.length < 8) {
      setErrorMessage('La contraseña debe tener al menos 8 caracteres.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorMessage('Las contraseñas ingresadas no coinciden.');
      return;
    }

    setIsSubmitting(true);
    const res = await setPasswordViaApi({
      email: setupEmail.trim(),
      token: setupToken.trim(),
      password: newPassword,
    });
    setIsSubmitting(false);

    if (!res.ok) {
      setErrorMessage(res.message);
      return;
    }

    // Limpiar query params de la URL tras activar la contraseña
    window.history.replaceState({}, document.title, window.location.pathname);
    onAuthenticated(res.session);
  };

  return (
    <div className={styles.backdrop}>
      <div className={styles.card}>
        <div className={styles.brandHeader}>
          <div className={styles.brandGroup}>
            <div className={styles.brandLogo}>S/</div>
            <div>
              <h1 className={styles.brandTitle}>EconomiController</h1>
              <p className={styles.brandSubtitle}>
                Telemetría del Impacto Temporal de cada Sol
              </p>
            </div>
          </div>
          <span className={styles.pilotBadge}>MARCHA BLANCA</span>
        </div>

        {mode !== 'SET_PASSWORD' && (
          <div className={styles.tabSwitcher}>
            <button
              type="button"
              className={`${styles.tabBtn} ${
                mode === 'LOGIN' ? styles.tabBtnActive : ''
              }`}
              onClick={() => switchMode('LOGIN')}
            >
              Iniciar Sesión
            </button>
            <button
              type="button"
              className={`${styles.tabBtn} ${
                mode === 'REGISTER' ? styles.tabBtnActive : ''
              }`}
              onClick={() => switchMode('REGISTER')}
            >
              Registrarse
            </button>
          </div>
        )}

        {/* MODO 1: INICIAR SESIÓN */}
        {mode === 'LOGIN' && (
          <>
            <h2 className={styles.sectionTitle}>
              Iniciar Sesión{' '}
              <InfoPopover title="Acceso Seguro Marcha Blanca">
                Ingresa con tu correo autorizado y la contraseña creada desde el enlace enviado vía Resend. En fase de Marcha Blanca solo operan los 2 correos autorizados del piloto.
              </InfoPopover>
            </h2>
            <p className={styles.sectionDesc}>
              Ingresa tus credenciales autorizadas.
            </p>

            {isRestrictedPublicAttempt && (
              <div className={styles.restrictedPublicBox}>
                <strong>🔒 Acceso restringido a Marcha Blanca (2 usuarios)</strong>
              </div>
            )}

            {errorMessage && !isRestrictedPublicAttempt && (
              <div className={styles.errorBox}>{errorMessage}</div>
            )}

            <form className={styles.form} onSubmit={handleLoginSubmit}>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="login-email">
                  CORREO ELECTRÓNICO
                </label>
                <input
                  id="login-email"
                  type="email"
                  required
                  placeholder="tu-correo@gmail.com"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  className={styles.input}
                />
              </div>

              <div className={styles.field}>
                <label className={styles.label} htmlFor="login-password">
                  CONTRASEÑA
                </label>
                <input
                  id="login-password"
                  type="password"
                  required
                  placeholder="••••••••"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  className={styles.input}
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className={styles.submitBtn}
              >
                {isSubmitting
                  ? 'Verificando credenciales...'
                  : 'Iniciar Sesión en EconomiController'}
              </button>
            </form>

            <div className={styles.helperFooter}>
              ¿Es tu primera vez?{' '}
              <button
                type="button"
                className={styles.linkBtn}
                onClick={() => switchMode('REGISTER')}
              >
                Regístrate aquí primero para recibir tu enlace de acceso
              </button>
            </div>
          </>
        )}

        {/* MODO 2: REGISTRO CONVENCIONAL + ENVÍO DE LINK CON RESEND */}
        {mode === 'REGISTER' && (
          <>
            <h2 className={styles.sectionTitle}>
              Registro de Cuenta{' '}
              <InfoPopover title="Acceso Seguro Marcha Blanca">
                Validaremos tu correo autorizado y te enviaremos un enlace vía Resend para crear tu contraseña e ingresar.
              </InfoPopover>
            </h2>
            <p className={styles.sectionDesc}>
              Recibirás un enlace por correo para crear tu clave.
            </p>

            {isRestrictedPublicAttempt && (
              <div className={styles.restrictedPublicBox}>
                <strong>
                  🚫 Correo {registerEmail} no autorizado en Marcha Blanca (2 usuarios)
                </strong>
              </div>
            )}

            {errorMessage && !isRestrictedPublicAttempt && (
              <div className={styles.errorBox}>{errorMessage}</div>
            )}

            {signupSuccessEmail ? (
              <div className={styles.successBox}>
                <strong style={{ fontSize: '0.96rem', display: 'block', marginBottom: '0.4rem' }}>
                  📧 ¡Revisa tu correo electrónico ({signupSuccessEmail})!
                </strong>
                <p style={{ margin: '0 0 0.6rem 0' }}>
                  Hemos enviado una notificación mediante <strong>Resend</strong>{' '}
                  con un enlace seguro para que crees tu contraseña personal.
                </p>
                <p style={{ margin: 0, fontSize: '0.8rem', color: '#d1fae5' }}>
                  Haz clic en el botón{' '}
                  <strong>&ldquo;Crear mi Contraseña e Ingresar&rdquo;</strong>{' '}
                  dentro del correo para definir tu clave y entrar directamente
                  a la aplicación.
                </p>
              </div>
            ) : (
              <form className={styles.form} onSubmit={handleRegisterSubmit}>
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="reg-fullname">
                    NOMBRES Y APELLIDOS
                  </label>
                  <input
                    id="reg-fullname"
                    type="text"
                    required
                    minLength={2}
                    placeholder="Ej. Alexander Meza Roque"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className={styles.input}
                  />
                </div>

                <div className={styles.field}>
                  <label className={styles.label} htmlFor="reg-email">
                    CORREO ELECTRÓNICO
                  </label>
                  <input
                    id="reg-email"
                    type="email"
                    required
                    placeholder="tu-correo@gmail.com"
                    value={registerEmail}
                    onChange={(e) => setRegisterEmail(e.target.value)}
                    className={styles.input}
                  />
                </div>

                <div className={styles.field}>
                  <label className={styles.label} htmlFor="reg-phone">
                    TELÉFONO / CELULAR
                  </label>
                  <input
                    id="reg-phone"
                    type="tel"
                    placeholder="Ej. +51 999 888 777"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className={styles.input}
                  />
                </div>

                <div className={styles.field}>
                  <label className={styles.label} htmlFor="reg-occupation">
                    OCUPACIÓN / PERFIL (OPCIONAL)
                  </label>
                  <input
                    id="reg-occupation"
                    type="text"
                    placeholder="Ej. Ingeniería de Software / Finanzas"
                    value={occupation}
                    onChange={(e) => setOccupation(e.target.value)}
                    className={styles.input}
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={styles.submitBtn}
                >
                  {isSubmitting
                    ? 'Enviando enlace a tu correo vía Resend...'
                    : 'Registrarme y Enviar Link de Contraseña'}
                </button>
              </form>
            )}

            <div className={styles.helperFooter}>
              ¿Ya creaste tu contraseña desde el correo?{' '}
              <button
                type="button"
                className={styles.linkBtn}
                onClick={() => switchMode('LOGIN')}
              >
                Inicia sesión aquí
              </button>
            </div>
          </>
        )}

        {/* MODO 3: CREAR CONTRASEÑA DESDE EL LINK DEL CORREO RESEND */}
        {mode === 'SET_PASSWORD' && (
          <>
            <h2 className={styles.sectionTitle}>
              Crea tu Contraseña{' '}
              <InfoPopover title="Activación de Cuenta">
                Tu correo fue verificado vía Resend. Define una contraseña de al menos 8 caracteres para ingresar.
              </InfoPopover>
            </h2>
            <p className={styles.sectionDesc}>
              Define tu clave para activar tu acceso.
            </p>

            {errorMessage && (
              <div className={styles.errorBox}>{errorMessage}</div>
            )}

            <form className={styles.form} onSubmit={handleSetPasswordSubmit}>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="setup-email">
                  CORREO VERIFICADO
                </label>
                <input
                  id="setup-email"
                  type="email"
                  readOnly
                  value={setupEmail}
                  onChange={(e) => setSetupEmail(e.target.value)}
                  className={`${styles.input} ${styles.inputReadonly}`}
                />
              </div>

              <div className={styles.field}>
                <label className={styles.label} htmlFor="setup-password">
                  NUEVA CONTRASEÑA (MÍNIMO 8 CARACTERES)
                </label>
                <input
                  id="setup-password"
                  type="password"
                  required
                  minLength={8}
                  placeholder="Ingresa tu nueva contraseña"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className={styles.input}
                />
              </div>

              <div className={styles.field}>
                <label className={styles.label} htmlFor="setup-confirm">
                  CONFIRMAR CONTRASEÑA
                </label>
                <input
                  id="setup-confirm"
                  type="password"
                  required
                  minLength={8}
                  placeholder="Repite tu contraseña"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={styles.input}
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className={styles.submitBtn}
              >
                {isSubmitting
                  ? 'Activando cuenta e iniciando sesión...'
                  : 'Guardar Contraseña e Ingresar a la App →'}
              </button>
            </form>

            <div className={styles.helperFooter}>
              <button
                type="button"
                className={styles.linkBtn}
                onClick={() => switchMode('LOGIN')}
              >
                Volver al inicio de sesión
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
